import React, { useEffect, useMemo, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Layout from '../Layout';
import ModalManager, { ModalCallbacks } from './ModalManager';
import PreviewPDF from './PreviewPDF';
import InsuranceAddonModal from './InsuranceAddonModal';
import VisaDetailsModal from '../PackageBuilder/modals/VisaDetailsModal';
import CustomFlightModal from '../PackageBuilder/modals/CustomFlightModal';
import ShareQuotationButton from '../PackageBuilder/ShareQuotationButton';
import RoomsTravellersDropdown from '../Common/RoomsTravellersDropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRole } from '../../hooks/usePermissions';
import { usePackageBuilderState } from '../../hooks/usePackageBuilderState';
import { usePackageSave } from '../../hooks/usePackageSave';
import { fetchPackagesPage } from '../../api/Packages';
import { loadAirportLocations } from '../../api/Location';
import { TravelPackage } from '../../types/TravelPackage';
import { Flight, Hotel, ItineraryItem, Transfer } from '../../types/Quotation';
import { LocationOption } from '../../types/Location';
import { InsuranceDetail, VisaDetail } from '../../types/PackageBuilder';
import { formatTime, getDatePlusDays } from '../../utils/date';
import { getHotelMealType } from '../../utils/hotelMealType';
import { buildDefaultQuotationRooms, normalizeQuotationRooms, QuotationRoomOccupancy, summarizeQuotationRooms } from './quotationRoomOccupancy';
import {
  isPackageBuilderLocationState,
  PackageBuilderLocationState,
} from '../PackageBuilder/contracts';
import {
  descriptionLimits,
  faqLimits,
  highlightLimits,
} from '../PackageBuilder/editors/sharedSectionProps';
import '../../css/QuotationBuilder.css';
import '../../css/PreviewPDF.css';
import '../../css/modal.css';

const DEFAULT_QUOTATION_MARKUP_PERCENT = 15;
const MIN_QUOTATION_MARKUP_PERCENT = 12;
const QUOTATION_GST_RATE = 0.05;

type WorkflowCardProps = {
  step: number;
  title: string;
  subtitle: string;
  children: React.ReactNode;
};

const WorkflowCard: React.FC<WorkflowCardProps> = ({ step, title, subtitle, children }) => (
  <section className="quotation-workflow-card">
    <div className="quotation-workflow-card__header">
      <div className="quotation-workflow-card__step">{step}</div>
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
    </div>
    {children}
  </section>
);

const getPackageImage = (pkg: TravelPackage) => (
  pkg.hero_image
  || pkg.gallery?.[0]
  || pkg.hotels?.[0]?.image
  || 'https://cdn.otaprime.com/logo-new.png'
);

const flattenFlights = (itinerary: ItineraryItem[]) =>
  itinerary.flatMap((day) => day.flights || []);

const flattenHotels = (itinerary: ItineraryItem[]) =>
  itinerary.flatMap((day, dayIndex) => (day.hotels || []).map((hotel) => ({ ...hotel, dayIndex })));

const flattenTransfers = (itinerary: ItineraryItem[]) =>
  itinerary.flatMap((day, dayIndex) => (day.transfers || []).map((transfer) => ({ ...transfer, dayIndex })));

const formatCabType = (value: string) => value.replace(/\s+/g, ' ').trim();

const formatMoney = (amount: number, currency: 'INR' | 'USD' = 'INR') => {
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol} ${Number(amount || 0).toLocaleString('en-IN')}`;
};

const formatDuration = (pkg?: TravelPackage) => {
  if (!pkg) return '';
  const nights = Number(pkg.durationNights ?? pkg.duration_nights ?? 0);
  const days = Number(pkg.durationDays ?? pkg.duration_days ?? 0);
  return `${nights} Nights / ${days} Days`;
};

const applyQuotationGst = (amount: number) => Math.round(Math.max(0, Number(amount || 0)) * (1 + QUOTATION_GST_RATE));

const QuotationWorkflowPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState: PackageBuilderLocationState | undefined = isPackageBuilderLocationState(location.state)
    ? location.state
    : undefined;

  const role = useRole();
  const step = locationState?.quotationStep === 'review' ? 'review' : 'builder';
  const packageToCopy = locationState?.packageToCopy;
  const packageFromDraft = locationState?.packageFromDraft;
  const sourceQueryId = typeof locationState?.queryId === 'number' ? locationState.queryId : undefined;

  const [packageSearch, setPackageSearch] = useState('');
  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagePage, setPackagePage] = useState(1);
  const [, setPackageTotalPages] = useState(1);
  const [hotelRooms, setHotelRooms] = useState<QuotationRoomOccupancy[]>(() => normalizeQuotationRooms(locationState?.hotelRooms || buildDefaultQuotationRooms()));

  const builderState = usePackageBuilderState({
    builderMode: 'quotation',
    sourceQueryId,
    packageToCopy,
    packageFromDraft,
    hotelRooms,
    highlightLimits,
    faqLimits,
  });

  const { coreState, pricingState, contentState, modalState, assetsState, derivedState, actions } = builderState;
  const quotationPdfRef = useRef<HTMLDivElement | null>(null);
  const quotationTotalPrice = useMemo(
    () => applyQuotationGst(derivedState.displayedTotal),
    [derivedState.displayedTotal]
  );
  const resolvePlaceIndexForDay = (dayIndex: number) => {
    const placeBoundaries = coreState.tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, place, idx) => {
      const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
      const end = start + Math.max(0, Number(place.nights || 0));
      acc.push({ start, end, idx });
      return acc;
    }, []);
    const hit = placeBoundaries.find((boundary) => dayIndex >= boundary.start && dayIndex < boundary.end);
    if (hit) return hit.idx;
    return Math.max(0, coreState.tripInfo.places.length - 1);
  };

  useEffect(() => {
    if (pricingState.markupType !== 'percent') {
      actions.setMarkupType('percent');
      return;
    }

    const currentMarkup = Number(pricingState.markupValue || 0);
    if (currentMarkup === 0) {
      actions.setMarkupValue(DEFAULT_QUOTATION_MARKUP_PERCENT);
      return;
    }

    if (currentMarkup < MIN_QUOTATION_MARKUP_PERCENT) {
      actions.setMarkupValue(MIN_QUOTATION_MARKUP_PERCENT);
    }
  }, [actions, pricingState.markupType, pricingState.markupValue]);

  const { buildPackagePayload } = usePackageSave({
    trip: {
      builderMode: 'quotation',
      tripInfo: coreState.tripInfo,
      itinerary: coreState.itinerary,
      nightsCount: coreState.nightsCount,
    },
    pricing: {
      finalTotal: derivedState.finalTotal,
      strikePrice: pricingState.strikePrice,
      pricePer: pricingState.pricePer,
      currency: pricingState.currency,
      hotelCategory: contentState.hotelCategory,
      rating: pricingState.rating,
      mealPlan: contentState.mealPlan,
      transferType: contentState.transferType,
      transferPricing: pricingState.transferPricing,
      flightsTotal: derivedState.flightsTotal,
      hotelsTotal: derivedState.hotelsTotal,
      activitiesTotal: derivedState.activitiesTotal,
      transfersTotal: derivedState.transfersTotal,
      addonsTotalForPackage: derivedState.addonsTotalForPackage,
      visaTotal: derivedState.visaTotal,
      insuranceTotal: derivedState.insuranceTotal,
      inclusionsTotal: derivedState.inclusionsTotal,
      markupType: pricingState.markupType,
      markupValue: pricingState.markupValue,
      markupAmount: derivedState.markupAmount,
    },
    content: {
      highlights: contentState.highlights,
      highlightLimits,
      descriptionLimits,
      faqLimits,
      idealFor: contentState.idealFor,
      idealVendor: contentState.idealVendor,
      searchTags: contentState.searchTags,
      relatedDestinations: contentState.relatedDestinations,
      relatedPackages: contentState.relatedPackages,
      exclusions: contentState.exclusions,
      addons: contentState.addons,
      reviews: contentState.reviews,
      faq: contentState.faq,
      isPublished: pricingState.isPublished,
      visaDetails: contentState.visaDetails,
      insuranceDetails: contentState.insuranceDetails,
      customVisaDetails: contentState.customVisaDetails,
      customInsuranceDetails: contentState.customInsuranceDetails,
      customFlights: contentState.customFlights,
      customActivityInclusions: contentState.customActivityInclusions,
    },
    media: {
      heroImageFile: assetsState.heroImageFile,
      galleryFiles: assetsState.galleryFiles,
      hotelImageFiles: assetsState.hotelImageFiles,
    },
    meta: {
      editingId: coreState.editingId,
      slug: derivedState.slug,
    },
  });

  useEffect(() => {
    if (!packageSearch.trim()) {
      setLoadingPackages(false);
      setPackages([]);
      setPackageTotalPages(1);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        setLoadingPackages(true);
        const response = await fetchPackagesPage({
          search: packageSearch.trim() || undefined,
          page: packagePage,
          pageSize: 6,
        });
        if (!active) return;
        setPackages(response.items || []);
        setPackageTotalPages(response.pagination.totalPages || 1);
      } catch {
        if (!active) return;
        setPackages([]);
        setPackageTotalPages(1);
      } finally {
        if (active) setLoadingPackages(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [packagePage, packageSearch]);

  const selectedPackage = packageToCopy || (
    packageFromDraft && typeof packageFromDraft === 'object'
      ? packageFromDraft as TravelPackage
      : undefined
  );
  const visiblePackages = selectedPackage ? [selectedPackage] : packages;
  const derivedEndDate = coreState.tripInfo.startDate ? getDatePlusDays(coreState.tripInfo.startDate, coreState.nightsCount) : '';
  const flightList = useMemo(() => flattenFlights(coreState.itinerary), [coreState.itinerary]);
  const hotelList = useMemo(() => flattenHotels(coreState.itinerary), [coreState.itinerary]);
  const transferList = useMemo(() => flattenTransfers(coreState.itinerary), [coreState.itinerary]);
  const totalPassengers = Number(coreState.tripInfo.travellers.adults || 0) + Number(coreState.tripInfo.travellers.children || 0);
  const hotelRoomSummary = useMemo(() => summarizeQuotationRooms(hotelRooms), [hotelRooms]);
  const hotelMealType = getHotelMealType(contentState.mealPlan);
  const packageCabOptions = builderState.quotationPackageState.transferRows;
  const selectedCabRowId = builderState.derivedState.quotationTransferCalculation?.sourceRowId;
  const selectedCabLabel = builderState.derivedState.quotationTransferCalculation?.cabType;

  const packagePayload = buildPackagePayload();
  const providerRef: Record<string, unknown> = useMemo(() => ({
    ...packagePayload,
    packageId: builderState.quotationPackageState.packageId || undefined,
    client: coreState.client,
    source: coreState.quotationSource || undefined,
    transferCalculation: builderState.derivedState.quotationTransferCalculation || undefined,
    transferOverride: pricingState.quotationTransferOverride,
    quotation: {
      origin: 'template',
      templateId: builderState.quotationPackageState.packageId || undefined,
      finalOverride: pricingState.quotationFinalOverride,
    },
  }), [
    packagePayload,
    builderState.quotationPackageState.packageId,
    builderState.derivedState.quotationTransferCalculation,
    coreState.client,
    coreState.quotationSource,
    pricingState.quotationFinalOverride,
    pricingState.quotationTransferOverride,
  ]);

  const pdfTripInfo = useMemo(() => ({
    ...coreState.tripInfo,
    totalPrice: quotationTotalPrice,
    hotelCategory: contentState.hotelCategory,
    transferType: contentState.transferType,
    inclusions: Array.from(new Set([
      ...(coreState.tripInfo.inclusions || []).map((value) => {
        const key = String(value || '').trim().toLowerCase();
        if (key === 'selected meals' || key === 'selected meal' || key === 'meals') return 'meal';
        if (key === 'sightseeing & activities' || key === 'sightseeing and activities' || key === 'activity') return 'activities';
        if (key === 'transfer' || key === 'transfers') return 'transfers';
        return key;
      }),
      ...((contentState.transferType || (builderState.derivedState.quotationTransferCalculation?.total || 0) > 0) ? ['transfers'] : []),
      ...(contentState.highlights.length > 0 ? ['activities'] : []),
      ...(contentState.visaDetails.length > 0 ? ['visa'] : []),
      ...(contentState.insuranceDetails.length > 0 ? ['insurance'] : []),
    ])),
  }), [
    builderState.derivedState.quotationTransferCalculation,
    contentState.highlights.length,
    contentState.hotelCategory,
    contentState.insuranceDetails.length,
    contentState.transferType,
    contentState.visaDetails.length,
    coreState.tripInfo,
    quotationTotalPrice,
  ]);

  const updateState = (next: Partial<PackageBuilderLocationState>) => {
    navigate('/quotation/builder', {
      replace: true,
      state: {
        ...locationState,
        ...next,
      },
    });
  };

  const loadAirportOptions = async (inputValue: string): Promise<LocationOption[]> => {
    try {
      const result = await loadAirportLocations(inputValue);
      const options = result || [];
      if (inputValue.trim() && options.length === 0) {
        return [{ label: inputValue.trim(), value: inputValue.trim(), type: 'airport' }];
      }
      return options;
    } catch {
      if (inputValue.trim()) {
        return [{ label: inputValue.trim(), value: inputValue.trim(), type: 'airport' }];
      }
      return [];
    }
  };

  const renderFlightSummary = (flight: Flight, index: number, onRemove: (dayIndex: number, flightIndex: number) => void) => {
    const dayIndex = coreState.itinerary.findIndex((day) => (day.flights || []).includes(flight));
    const flightIndex = dayIndex >= 0 ? (coreState.itinerary[dayIndex].flights || []).indexOf(flight) : -1;

    return (
      <div key={`${flight.flightNumber}-${index}`} className="quotation-choice-row">
        <div className="quotation-choice-row__main">
          <div className="quotation-choice-row__logo">
            {flight.provider ? (
              <img src={`https://cdn.otaprime.com/airline/${flight.provider}.gif`} alt={flight.airline} />
            ) : (
              <span>{flight.airline.slice(0, 2).toUpperCase()}</span>
            )}
          </div>
          <div>
            <h4>{flight.airline}</h4>
            <p>{flight.from} to {flight.to}</p>
          </div>
        </div>
        <div className="quotation-choice-row__meta">
          <strong>{formatTime(flight.departureTime)} - {formatTime(flight.arrivalTime)}</strong>
          <span>{flight.duration} • {flight.stops > 0 ? `${flight.stops} stop` : 'Non stop'}</span>
        </div>
        <div className="quotation-stay-row__actions">
          <div className="quotation-choice-row__price">{formatMoney(flight.price, pricingState.currency)}</div>
          {dayIndex >= 0 && flightIndex >= 0 && (
            <button
              type="button"
              className="quotation-action-btn"
              onClick={() => onRemove(dayIndex, flightIndex)}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderHotelSummary = (hotel: Hotel & { dayIndex?: number }, index: number, onChange: (dayIndex: number) => void) => (
    <div key={`${hotel.hotelId}-${index}`} className="quotation-stay-row">
      <img src={hotel.thumbnail || 'https://cdn.otaprime.com/logo-new.png'} alt={hotel.name} />
      <div className="quotation-stay-row__body">
        <div>
          <h4>{hotel.name}</h4>
          <p>{hotel.starRating} Star • {hotel.roomType || 'Selected room'}</p>
        </div>
        <div className="quotation-stay-row__actions">
          <strong>{formatMoney(hotel.display_price || hotel.price, pricingState.currency)}</strong>
          <button type="button" className="quotation-action-btn" onClick={() => onChange(resolvePlaceIndexForDay(hotel.dayIndex || 0))}>Change Hotel</button>
        </div>
      </div>
    </div>
  );

  const renderTransferSummary = (transfer: Transfer & { dayIndex?: number }, index: number, onChange: (dayIndex: number) => void) => (
    <div key={`${transfer.name}-${index}`} className="quotation-transfer-row">
      <div>
        <h4>{transfer.vehicle || transfer.name || 'Transfer'}</h4>
        <p>
          {(transfer.pickupFromAirport ? (transfer.airportName || transfer.airportCode) : (transfer.hotelName || transfer.hotelCode)) || 'Pickup'}
          {' '}to{' '}
          {(transfer.pickupFromAirport ? (transfer.hotelName || transfer.hotelCode) : (transfer.airportName || transfer.airportCode)) || 'Drop'}
        </p>
      </div>
      <div className="quotation-stay-row__actions">
        <strong>{formatMoney(Number(transfer.price || 0), pricingState.currency)}</strong>
        <button type="button" className="quotation-action-btn" onClick={() => onChange(transfer.dayIndex || 0)}>Change Cab</button>
      </div>
    </div>
  );

  const handleCabSelection = (rowId?: string, tempId?: string) => {
    const selectedRowKey = rowId || tempId;
    if (!selectedRowKey) return;
    actions.setQuotationTransferOverride(null);
    actions.setQuotationTransferRows((prev) => prev.map((row) => {
      const currentKey = row.id || row.tempId;
      return {
        ...row,
        isActive: currentKey === selectedRowKey,
      };
    }));
  };

  return (
    <Layout>
      <ModalManager
        nightsCount={coreState.nightsCount}
        tripInfo={coreState.tripInfo}
        itinerary={coreState.itinerary}
        setItinerary={actions.setItinerary}
        startDate={coreState.tripInfo.startDate}
        travellers={coreState.tripInfo.travellers}
        hotelCache={assetsState.hotelCache}
        hotelMealTypeOverride={hotelMealType || undefined}
        hotelRooms={hotelRooms}
        flightReselectTrigger={builderState.quotationRepriceState.flightReselectTrigger}
        onFlightReselected={builderState.quotationRepriceState.markFlightsReselected}
      >
        {({
          openFlightModal,
          openHotelModal,
          openTransferModal,
          removeFlight,
        }: ModalCallbacks) => (
          <div className="quotation-workflow">
            <div className="quotation-workflow__hero">
              <div>
                <span className="quotation-workflow__eyebrow">Quotation Workspace</span>
                <h1>Quotation Builder</h1>
              </div>
            </div>

            <div className="quotation-workflow__grid quotation-workflow__grid--top">
              <WorkflowCard step={1} title="Select Base Package" subtitle="Choose a base package to build the quotation">
                <div className="quotation-package-search">
                  <input
                    type="text"
                    value={packageSearch}
                    onChange={(e) => {
                      setPackageSearch(e.target.value);
                      setPackagePage(1);
                    }}
                    placeholder="Search packages..."
                  />
                </div>
                <div className="quotation-package-list">
                  {loadingPackages && <div className="quotation-empty-state">Loading packages...</div>}
                  {!loadingPackages && visiblePackages.map((pkg) => {
                    const active = selectedPackage?.id === pkg.id;
                    return (
                      <button
                        key={pkg.id || pkg.slug}
                        type="button"
                        className={`quotation-package-card${active ? ' active' : ''}`}
                        onClick={() => updateState({ packageToCopy: pkg, quotationStep: 'builder' })}
                      >
                        <img src={getPackageImage(pkg)} alt={pkg.title} />
                        <div className="quotation-package-card__body">
                          <div className="quotation-package-card__top">
                            <div>
                              <h3>{pkg.title}</h3>
                              <p>{formatDuration(pkg)}</p>
                            </div>
                          </div>
                          <div className="quotation-package-card__tags">
                            {(pkg.inclusions || []).slice(0, 4).map((item) => (
                              <span key={item}>{item}</span>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {!loadingPackages && packageSearch.trim() && visiblePackages.length === 0 && (
                    <div className="quotation-empty-state">No packages found.</div>
                  )}
                </div>
              </WorkflowCard>

              <WorkflowCard step={2} title="Trip Details" subtitle="Enter trip details to proceed">
                <div className="quotation-form-grid">
                  <div className="quotation-field quotation-field--full">
                    <label>Base Package</label>
                    <div className="quotation-readonly">{selectedPackage?.title || 'Select a package from the left'}</div>
                  </div>
                  <div className="quotation-field">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={coreState.tripInfo.startDate}
                      onChange={(e) => actions.handleTripInfoChange(e)}
                      name="startDate"
                    />
                  </div>
                  <div className="quotation-field">
                    <label>End Date (Derived)</label>
                    <input type="date" value={derivedEndDate} disabled readOnly />
                  </div>
                  <div className="quotation-duration">
                    Duration: {coreState.nightsCount} Nights / {coreState.nightsCount + 1} Days
                  </div>
                  <div className="quotation-field quotation-field--full">
                    <label>Rooms & Travellers</label>
                    <RoomsTravellersDropdown value={hotelRooms} onChange={setHotelRooms} />
                  </div>
                  <div className="quotation-field quotation-field--full">
                    <label>Starting From</label>
                    <AsyncSelect<LocationOption, false>
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAirportOptions}
                      getOptionLabel={(o) => o.label}
                      getOptionValue={(o) => o.value}
                      onChange={(opt) => actions.handleTripInfoChange({ name: 'from', value: opt ? { label: opt.label, value: opt.value } : undefined })}
                      value={coreState.tripInfo.from ? { label: coreState.tripInfo.from.label, value: coreState.tripInfo.from.value, type: 'airport' } : null}
                      placeholder="Type a city or airport"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                </div>
              </WorkflowCard>

              <WorkflowCard step={3} title="Select Flights" subtitle="Choose your preferred flights">
                {builderState.quotationRepriceState.flightReselectRequired && (
                  <div className="quotation-warning">Travel dates or passengers changed. Please reselect flights.</div>
                )}
                <div className="quotation-section-actions">
                  <button type="button" className="quotation-primary-btn" onClick={() => openFlightModal(false)}>
                    {flightList.length ? 'Change Flights' : 'Select Flights'}
                  </button>
                </div>
                <div className="quotation-choice-list">
                  {flightList.length > 0 ? flightList.map((flight, index) => renderFlightSummary(flight, index, removeFlight)) : (
                    <div className="quotation-empty-state">No flights selected yet.</div>
                  )}
                </div>
              </WorkflowCard>
            </div>

            <div className="quotation-workflow__grid quotation-workflow__grid--bottom">
              <WorkflowCard step={4} title="Customize the Trip" subtitle="Modify hotel and cab as per customer's preference">
                <div className="quotation-customise-block">
                  <div className="quotation-customise-section">
                    <div className="quotation-customise-section__head">
                      <h3>Hotel</h3>
                    </div>
                    {hotelList.length > 0 ? hotelList.map((hotel, index) => renderHotelSummary(hotel, index, openHotelModal)) : (
                      <div className="quotation-empty-state">No hotels selected yet.</div>
                    )}
                  </div>

                  <div className="quotation-customise-section">
                    <div className="quotation-customise-section__head">
                      <h3>Cab / Transfer</h3>
                    </div>
                    {packageCabOptions.length > 0 ? (
                      <div className="quotation-choice-list">
                        {packageCabOptions
                          .filter((row) => !row.isDeleted)
                          .map((row) => {
                            const rowKey = row.id || row.tempId || row.cabType;
                            const occupancy = Number(row.maxOccupancy || 0);
                            const disabled = occupancy > 0 && occupancy < Math.max(1, totalPassengers);
                            const isSelected = selectedCabRowId
                              ? selectedCabRowId === (row.id || row.tempId)
                              : row.isActive && formatCabType(row.cabType) === formatCabType(selectedCabLabel || '');

                            return (
                              <label
                                key={rowKey}
                                className={`quotation-choice-row quotation-choice-row--selectable${isSelected ? ' active' : ''}${disabled ? ' disabled' : ''}`}
                              >
                                <div className="quotation-choice-row__main">
                                  <div className="quotation-choice-row__logo">
                                    <span>{occupancy || '-'}</span>
                                  </div>
                                  <div>
                                    <h4>{row.cabType}</h4>
                                    <p>Up to {occupancy} passengers</p>
                                  </div>
                                </div>
                                <div className="quotation-choice-row__meta">
                                  <strong>{formatMoney(Number(row.price || 0), pricingState.currency)}</strong>
                                  <span>{disabled ? `Needs ${totalPassengers} pax support` : 'Package cab pricing'}</span>
                                </div>
                                <input
                                  type="radio"
                                  name="quotation-cab-selection"
                                  checked={isSelected}
                                  disabled={disabled}
                                  onChange={() => handleCabSelection(row.id, row.tempId)}
                                />
                              </label>
                            );
                          })}
                      </div>
                    ) : transferList.length > 0 ? transferList.map((transfer, index) => renderTransferSummary(transfer, index, openTransferModal)) : (
                      <div className="quotation-empty-state">No cab pricing available for this package yet.</div>
                    )}
                  </div>
                </div>
              </WorkflowCard>

              <WorkflowCard step={5} title="Review & Confirm" subtitle="Review your itinerary and proceed">
                <div className="quotation-review-list">
                  <div className="quotation-review-row"><span>Base Package</span><strong>{selectedPackage?.title || '-'}</strong></div>
                  <div className="quotation-review-row"><span>Travel Dates</span><strong>{coreState.tripInfo.startDate || '-'} {derivedEndDate ? `- ${derivedEndDate}` : ''}</strong></div>
                  <div className="quotation-review-row"><span>Rooms & Travellers</span><strong>{hotelRoomSummary}</strong></div>
                  <div className="quotation-review-row"><span>Starting From</span><strong>{coreState.tripInfo.from?.label || '-'}</strong></div>
                </div>

                <div className="quotation-field quotation-field--full" style={{ marginTop: 16 }}>
                  <label>Markup (%)</label>
                  <input
                    type="number"
                    min={MIN_QUOTATION_MARKUP_PERCENT}
                    step="1"
                    value={pricingState.markupType === 'percent' ? pricingState.markupValue : DEFAULT_QUOTATION_MARKUP_PERCENT}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const nextValue = raw === ''
                        ? DEFAULT_QUOTATION_MARKUP_PERCENT
                        : Math.max(MIN_QUOTATION_MARKUP_PERCENT, Number(raw || 0));
                      actions.setMarkupType('percent');
                      actions.setMarkupValue(nextValue);
                    }}
                    placeholder={`${DEFAULT_QUOTATION_MARKUP_PERCENT}`}
                  />
                <div className="quotation-empty-state" style={{ marginTop: 8 }}>
                    Default markup is {DEFAULT_QUOTATION_MARKUP_PERCENT}%. Minimum allowed markup is {MIN_QUOTATION_MARKUP_PERCENT}%. GST at 5% is applied to the final amount after markup.
                  </div>
                </div>

                <div className="quotation-summary-panels">
                  <div className="quotation-summary-panel">
                    <span>Flights</span>
                    <strong>{formatMoney(derivedState.flightsTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Hotel</span>
                    <strong>{formatMoney(derivedState.hotelsTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Cab / Transfer</span>
                    <strong>{formatMoney(derivedState.transfersTotal, pricingState.currency)}</strong>
                  </div>
                </div>

                <div className="quotation-total-card">
                  <span>Total Package Price (incl. GST)</span>
                  <strong>{formatMoney(quotationTotalPrice, pricingState.currency)}</strong>
                </div>

                <div className="quotation-section-actions">
                  <button type="button" className="quotation-primary-btn" onClick={() => updateState({ quotationStep: 'review' })}>
                    Proceed to PDF Preview
                  </button>
                </div>
              </WorkflowCard>

              <WorkflowCard step={6} title="Quotation Preview (PDF)" subtitle="Share or download the final quotation">
                {step !== 'review' && (
                  <div className="quotation-empty-state">Proceed to PDF Preview to render the customer-facing quotation.</div>
                )}
                {step === 'review' && (
                  <>
                    <div className="quotation-preview-actions">
                      {role !== 'sales' && (
                        <>
                          <ShareQuotationButton
                            nightsCount={coreState.nightsCount}
                            queryId={sourceQueryId}
                            startDate={coreState.tripInfo.startDate}
                            hotelRooms={hotelRooms}
                            totalPrice={quotationTotalPrice}
                            providerRef={providerRef}
                            pdfRef={quotationPdfRef}
                            currency={pricingState.currency}
                            title={coreState.tripInfo.title}
                            client={coreState.client}
                            customerId={coreState.queryCustomerId}
                            assignedId={coreState.queryAssignedId}
                            createdBy={coreState.queryCreatedBy}
                            label="Download"
                            download
                            shareDraft={false}
                            className="quotation-secondary-btn"
                          />
                          <ShareQuotationButton
                            nightsCount={coreState.nightsCount}
                            queryId={sourceQueryId}
                            startDate={coreState.tripInfo.startDate}
                            hotelRooms={hotelRooms}
                            totalPrice={quotationTotalPrice}
                            providerRef={providerRef}
                            pdfRef={quotationPdfRef}
                            currency={pricingState.currency}
                            title={coreState.tripInfo.title}
                            client={coreState.client}
                            customerId={coreState.queryCustomerId}
                            assignedId={coreState.queryAssignedId}
                            createdBy={coreState.queryCreatedBy}
                            label="Share"
                            shareDraft
                            className="quotation-primary-btn"
                          />
                        </>
                      )}
                    </div>
                    {!sourceQueryId && (
                      <div className="quotation-warning quotation-warning--soft">
                        This quotation is not attached to a query yet. Download is available; share stays disabled.
                      </div>
                    )}
                    <div className="quotation-pdf-shell">
                      <div ref={quotationPdfRef}>
                        <PreviewPDF
                          client={coreState.client}
                          tripInfo={pdfTripInfo}
                          itinerary={coreState.itinerary}
                          quotationCreatedAt={coreState.quotationCreatedAt}
                          generalRules=""
                          highlights={(contentState.highlights || []).join(', ')}
                          highlightImages={[]}
                          inclusionImages={[]}
                          itineraryImages={[]}
                          addons={contentState.addons}
                          visaDetails={contentState.visaDetails}
                          insuranceDetails={contentState.insuranceDetails}
                          exclusions={contentState.exclusions}
                          mealPlan={contentState.mealPlan}
                          selectedHotelRoomCount={hotelRooms.length}
                          selectedCab={{
                            cabType: builderState.derivedState.quotationTransferCalculation?.cabType,
                            maxOccupancy: builderState.derivedState.quotationTransferCalculation?.maxOccupancy,
                            transferType: contentState.transferType,
                          }}
                          paymentCalendar={{
                            flightCost: derivedState.flightsTotal,
                            hotelCost: derivedState.hotelsTotal,
                            cabCost: derivedState.transfersTotal,
                            totalPackageCost: quotationTotalPrice,
                            currency: pricingState.currency,
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </WorkflowCard>
            </div>

            {modalState.showVisaModal && (
              <VisaDetailsModal
                currency={pricingState.currency}
                onClose={() => actions.setShowVisaModal(false)}
                onSave={(detail: VisaDetail) => {
                  actions.setVisaDetails((prev) => [...prev, detail]);
                  actions.setShowVisaModal(false);
                }}
              />
            )}

            {modalState.showInsuranceModal && (
              <InsuranceAddonModal
                currency={pricingState.currency}
                travelStartDate={coreState.tripInfo.startDate}
                nightsCount={coreState.nightsCount}
                noOfPax={derivedState.totalPax}
                onClose={() => actions.setShowInsuranceModal(false)}
                onSave={(addon: InsuranceDetail) => {
                  actions.setInsuranceDetails((prev) => [...prev, addon]);
                  actions.setShowInsuranceModal(false);
                }}
              />
            )}

            {modalState.showCustomVisaModal && (
              <VisaDetailsModal
                currency={pricingState.currency}
                onClose={() => actions.setShowCustomVisaModal(false)}
                onSave={(detail: VisaDetail) => {
                  actions.setCustomVisaDetails((prev) => [...prev, detail]);
                  actions.setShowCustomVisaModal(false);
                }}
              />
            )}

            {modalState.showCustomInsuranceModal && (
              <InsuranceAddonModal
                currency={pricingState.currency}
                travelStartDate={coreState.tripInfo.startDate}
                nightsCount={coreState.nightsCount}
                noOfPax={derivedState.totalPax}
                onClose={() => actions.setShowCustomInsuranceModal(false)}
                onSave={(addon: InsuranceDetail) => {
                  actions.setCustomInsuranceDetails((prev) => [...prev, addon]);
                  actions.setShowCustomInsuranceModal(false);
                }}
              />
            )}

            {modalState.showCustomFlightModal && (
              <CustomFlightModal
                onClose={() => actions.setShowCustomFlightModal(false)}
                onSave={(flight) => {
                  actions.setCustomFlights((prev) => [...prev, flight]);
                  actions.setShowCustomFlightModal(false);
                }}
              />
            )}

          </div>
        )}
      </ModalManager>
    </Layout>
  );
};

export default QuotationWorkflowPage;
